import { Upload } from 'lucide-react';
import { useId, useRef, type ComponentProps, type ReactNode } from 'react';

import { Button } from '@nucleus-commerce/ui/components/button';

import { ACCEPTED_MEDIA_TYPES } from '../api/media-api';

export interface MediaUploadButtonProps extends Omit<
  ComponentProps<typeof Button>,
  'onClick' | 'children'
> {
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  children: ReactNode;
  /** Restrict to a subset of the accepted types, e.g. images only for a product gallery. */
  accept?: readonly string[];
}

/** A button that opens the file picker; the hidden input is reset so the same file can be re-chosen. */
export function MediaUploadButton({
  onFiles,
  multiple = true,
  children,
  accept = ACCEPTED_MEDIA_TYPES,
  ...button
}: MediaUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();
  return (
    <>
      <input
        ref={inputRef}
        id={id}
        type="file"
        className="sr-only"
        tabIndex={-1}
        multiple={multiple}
        accept={accept.join(',')}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />
      <Button type="button" {...button} onClick={() => inputRef.current?.click()}>
        <Upload aria-hidden />
        {children}
      </Button>
    </>
  );
}
